import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useId,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { ensurePortalRoot, type Placement } from '../shared/index.js';
import {
  MOBILE_FLOATING_PANEL_STYLE,
  useMobileFloatingSheet,
} from '../shared/mobileFloating.js';
import { useFloating } from '../../hooks/useFloating.js';
import { useOutsideClick } from '../../hooks/useOutsideClick.js';
import { useEscapeStack } from '../../hooks/useEscapeStack.js';
import { Menu } from './Menu.js';
import type { MenuEntry } from './types.js';

export interface DropdownMenuProps {
  children: ReactElement<HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> }>;
  items: readonly MenuEntry[];
  placement?: Placement;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Close on item activation. Default true. */
  closeOnSelect?: boolean;
  'aria-label'?: string;
  className?: string;
  id?: string;
}

/** Button-triggered dropdown menu. Anchors a `<Menu>` to its child element via
 *  portal. Positioning delegated to `useFloating`; outside-click to
 *  `useOutsideClick`. */
export function DropdownMenu(props: DropdownMenuProps): ReactNode {
  const {
    children,
    items,
    placement = 'bottom-start',
    open: controlledOpen,
    defaultOpen = false,
    onOpenChange,
    closeOnSelect = true,
    'aria-label': ariaLabel,
    className,
    id,
  } = props;

  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const anchorRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const reactId = useId();
  const panelId = id ?? `${reactId}-menu`;

  const { setAnchor, setFloating, rect, floatingStyle, maxHeight } = useFloating({
    open,
    placement,
    gap: 6,
  });
  const mobileSheet = useMobileFloatingSheet(open);

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const close = useCallback(
    (returnFocus = true) => {
      setOpen(false);
      if (returnFocus) anchorRef.current?.focus();
    },
    [setOpen],
  );

  useOutsideClick(
    panelRef,
    useCallback(() => {
      close(false);
    }, [close]),
    { enabled: open, extraRefs: [anchorRef] },
  );

  // Escape via the shared modal stack — only the topmost layer responds.
  useEscapeStack(
    useCallback(() => {
      close();
    }, [close]),
    open,
  );

  const child = Children.only(children);
  if (!isValidElement(child)) return child;

  const prevRef = (child as ReactElement<{ ref?: Ref<HTMLElement> }>).props.ref;
  const mergedAnchorRef = (node: HTMLElement | null): void => {
    anchorRef.current = node;
    setAnchor(node);
    if (typeof prevRef === 'function') prevRef(node);
    else if (prevRef !== undefined && prevRef !== null) {
      (prevRef as { current: HTMLElement | null }).current = node;
    }
  };

  const mergedPanelRef = (node: HTMLDivElement | null): void => {
    panelRef.current = node;
    setFloating(node);
  };

  const existing = child.props;
  const triggerProps: HTMLAttributes<HTMLElement> & { ref: Ref<HTMLElement> } = {
    ref: mergedAnchorRef,
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? panelId : undefined,
    onClick: (e) => {
      existing.onClick?.(e);
      setOpen(!open);
    },
    onKeyDown: (e) => {
      existing.onKeyDown?.(e);
      if (
        !open &&
        (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ')
      ) {
        e.preventDefault();
        setOpen(true);
      }
    },
  };

  const portalRoot = ensurePortalRoot();

  const handleSelect = (): void => {
    if (closeOnSelect) close();
  };

  return (
    <>
      {cloneElement(child, triggerProps)}
      {open && portalRoot !== null
        ? createPortal(
            <div
              ref={mergedPanelRef}
              id={panelId}
              data-component="DropdownMenu"
              data-mobile-sheet={mobileSheet ? 'true' : undefined}
              className={
                ['vq-dropdown-menu-portal', className].filter(Boolean).join(' ')
              }
              style={
                mobileSheet
                  ? ({
                      ...MOBILE_FLOATING_PANEL_STYLE,
                      ['--vq-menu-max-h' as string]: 'min(70vh, calc(100vh - 32px))',
                    } as Record<string, string | number>)
                  : ({
                      ...floatingStyle,
                      // Pass viewport availableHeight to the inner Menu via a
                      // CSS var. .vq-menu uses min(60vh, var(--vq-menu-max-h))
                      // so it shrinks when the trigger sits near a viewport
                      // edge, but never grows past the design ceiling.
                      ...(maxHeight > 0
                        ? { ['--vq-menu-max-h' as string]: `${String(maxHeight)}px` }
                        : {}),
                    } as Record<string, string | number>)
              }
            >
              <Menu
                items={items}
                onItemSelect={handleSelect}
                autoFocus
                {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
                {...(rect?.side !== undefined ? { 'data-side': rect.side } : {})}
              />
            </div>,
            portalRoot,
          )
        : null}
    </>
  );
}
